/* eslint-disable rxjs-angular/prefer-takeuntil */
import { ViewportScroller } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { PlatformService } from '@core/platform';
import type { Subscription } from 'rxjs';
import { timer } from 'rxjs';
import { audit, debounce, filter, map } from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';
import {
  JssStateActionTypes,
  type JssStateAllTopLevelPlaceholdersLoaded,
} from '@core/jss-routing';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'wl-scroll-manager',
  template: '',
})
export class ScrollManagerComponent implements OnInit {
  subscription: Subscription | undefined;

  readonly #viewportScroller = inject(ViewportScroller);
  readonly #platformService = inject(PlatformService);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #destroyRef = inject(DestroyRef);
  readonly #actions$ = inject(Actions);
  readonly #router = inject(Router);

  readonly #navigationChanges = this.#router.events.pipe(
    filter((event) => event instanceof NavigationEnd)
  );

  public readonly allTopLevelPlaceholdersLoaded$ = this.#actions$.pipe(
    ofType<JssStateAllTopLevelPlaceholdersLoaded>(
      JssStateActionTypes.ALL_TOP_LEVEL_PLACEHOLDERS_LOADED
    )
  );

  #handleScrollOnNavigation() {
    this.subscription = this.#navigationChanges
      .pipe(
        audit(() => this.allTopLevelPlaceholdersLoaded$),
        /**
         * There is a 250ms delay before scrolling for 2 reasons:
         *   1. When the (loaded) event is emitted from <sc-placeholder>, it is only
         *      markedForCheck in the ChangeDetector, it still has to run.
         *   2. There could also be items in the placeholder that need to lazy-load,
         *      there is currently no way to wait for that.
         */
        debounce(() => timer(250)),
        map(() => {
          const { fragment } = this.#activatedRoute.snapshot;
          if (fragment) {
            this.#viewportScroller.scrollToAnchor(fragment);
          } else {
            // If there is an actual route change that has no fragment, it means we want to scroll to [0,0]
            // NOTE: This means scroll position restoration is not supported in our set-up, we always
            //       scroll to the top after every navigation (after 250ms)
            this.#viewportScroller.scrollToPosition([0, 0]);
          }
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  ngOnInit() {
    if (this.#platformService.isClient()) {
      this.#handleScrollOnNavigation();
    }
  }
}
